/* eslint-disable max-len */
import { Box, Card, Grid, Typography } from '@mui/material';
import { SubscriptionType } from '@pm/shared';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, memo, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { api } from '../../services';
import { PharmacyPostDataIPC } from '../../services/types';
import { IPCNav } from './components/IPCNav/IPCNav';
import { IPCStep1 } from './components/IPCStep1/IPCStep1';
import { IPCStep2 } from './components/IPCStep2/IPCStep2';
import { IntroSchema } from '../Intro/components/FormContent/IntroSchema';
import pms from '../../assets/json/pms.json';

// Combined form values for both steps
type IPCFormValues = {
	isIPC: boolean;
	// Step 1 fields
	firstName: string;
	lastName: string;
	position: string;
	pharmacyName: string;
	npiNumber: string;
	phone: string;
	email: string;
	password?: string;
	confirmPassword: string;
	termsAccepted: boolean;
	reCaptchaToken: string | null;
	emailEnabled: boolean;
	pushEnabled: boolean;
	isMultiStoreOwner: boolean;
	// Step 2 fields
	companyLegalName: string;
	pharmacyDbaName: string;
	pharmacyPhoneNumber: string;
	pharmacyEmail: string;
	streetAddress: string;
	city: string;
	state: string;
	zipCode: string;
	dea: string;
	ncpdp: string;
	statePharmacyLicense: string;
	pharmacyManagementSystem: string;
	primaryAccountNumber: string;
	ipcAccountNumber: string;
};

const ipcPositions = [
	'Owner',
	'Pharmacist',
	'Purchaser',
	'PIC',
	'Technician',
	'Accounts Payable',
	'Other',
];

// Step 1 validation schema
// Phone number format validation pattern from regular onboarding
const phoneNumberRegExp = /\([1-9]\d\d\)\s\d\d\d-\d\d\d\d/;

const step1ValidationSchema = Yup.object({
	firstName: Yup.string().required('First name is required'),
	lastName: Yup.string().required('Last name is required'), 
	position: Yup.string().required('Position is required'),
	npiNumber: Yup.string()
		.required('NPI Number is required')
		.matches(/^\d{10}$/, 'NPI Number must be exactly 10 digits'),
	email: Yup.string()
		.email('Invalid email address format')
		.required('Email address is required'),
	phone: Yup.string()
		.matches(phoneNumberRegExp, 'Phone number format must be (XXX) XXX-XXXX')
		.required('Phone number is required'),
	password: IntroSchema.fields.password,
	confirmPassword: IntroSchema.fields.confirmPassword,
	termsAccepted: Yup.boolean()
		.oneOf([true], 'You must accept the Terms and Privacy Policy')
		.required('You must accept the Terms and Privacy Policy'),
	reCaptchaToken: Yup.string().nullable().required('Please complete the reCAPTCHA verification'),
});

// Step 2 validation schema
const step2ValidationSchema = Yup.object({
	companyLegalName: Yup.string().required('Company legal name is required'),
	pharmacyDbaName: Yup.string().required('Pharmacy DBA name is required'),
	pharmacyPhoneNumber: Yup.string()
		.matches(phoneNumberRegExp, 'Phone number format must be (XXX) XXX-XXXX')
		.required('Pharmacy phone number is required'),
	pharmacyEmail: Yup.string()
		.email('Invalid pharmacy email address format')
		.required('Pharmacy email address is required'),
	streetAddress: Yup.string().required('Street address is required'),
	city: Yup.string().required('City is required'),
	state: Yup.string().required('State is required'),
	zipCode: Yup.string()
		.required('ZIP code is required')
		.matches(/^\d{5}$/, 'ZIP code must be exactly 5 digits'),
	dea: Yup.string()
		.required('DEA number is required')
		.matches(/^[A-Z]{2}\d{7}$/, 'DEA format must be two letters followed by 7 digits (e.g., AB1234567)'),
	ncpdp: Yup.string(),
	statePharmacyLicense: Yup.string(),
	pharmacyManagementSystem: Yup.string()
		.required('Pharmacy management system is required')
		.notOneOf(['Select'], 'Please select a pharmacy management system'),
	primaryAccountNumber: Yup.string().required('Primary account number is required'),
	ipcAccountNumber: Yup.string().required('IPC account number is required'),
});

export const IPC: FC = memo(() => {
	const [currentStep, setCurrentStep] = useState<number>(1);
	const [onboardJWT, setOnboardJWT] = useState<string>('');
	const [onboardId, setOnboardId] = useState<string>('');
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	const { enqueueSnackbar } = useSnackbar();

	// Reset reCaptchaToken when returning to Step 1
	useEffect(() => {
		if (currentStep === 1) {
			formik.setFieldValue('reCaptchaToken', null);
		}
	}, [currentStep]);

	const getOnboardJWT = async (email: string) => {
		try {
			const onboardRes = await api.post.rest.onboard({
				email: email,
			});
			setOnboardJWT(onboardRes.jwt);
			setOnboardId(onboardRes._id);
			localStorage.setItem('onboardJWT', onboardRes.jwt);
			return onboardRes;
		} catch (error: any) {
			console.error('Failed to get onboard JWT:', error);
			enqueueSnackbar('Failed to initialize form. Please try again.', { variant: 'error' });
			throw error;
		}
	};

	const initialValues: IPCFormValues = {
		// Step 1 initial values
		firstName: '',
		lastName: '',
		position: '',
		pharmacyName: '',
		npiNumber: '',
		phone: '',
		email: '',
		password: undefined,
		confirmPassword: '',
		termsAccepted: false,
		reCaptchaToken: null,
		emailEnabled: false,
		pushEnabled: false,
		isMultiStoreOwner: false,
		// Step 2 initial values
		companyLegalName: '',
		pharmacyDbaName: '',
		pharmacyPhoneNumber: '',
		pharmacyEmail: '',
		streetAddress: '',
		city: '',
		state: '',
		zipCode: '',
		dea: '',
		ncpdp: '',
		statePharmacyLicense: '',
		pharmacyManagementSystem: '',
		primaryAccountNumber: '',
		ipcAccountNumber: '',
		isIPC: true
	};

	const formik = useFormik<IPCFormValues>({
		initialValues,
		validationSchema: currentStep === 1 ? step1ValidationSchema : step2ValidationSchema,
		onSubmit: async (values) => {
			try {
				const pharmacyPostData: PharmacyPostDataIPC = {
					isIPC: values.isIPC,
					onboardId: onboardId,
					isMultiStoreOwner: values.isMultiStoreOwner,
					coupon: undefined,
					signer: {
						name: `${values.firstName} ${values.lastName}`.trim(),
						title: values.position
					},
					user: {
						firstName: values.firstName,
						lastName: values.lastName,
						email: values.email,
						emailEnabled: values.emailEnabled,
						pushEnabled: values.pushEnabled,
						password: values.password,
					},
					firstName: values.firstName,
					lastName: values.lastName,
					title: values.position,
					name: values.pharmacyDbaName,
					legalName: values.companyLegalName,
					stateOfIncorporation: '',
					email: values.pharmacyEmail,
					phone: values.pharmacyPhoneNumber,
					fax: '',
					address: values.streetAddress,
					city: values.city,
					state: values.state,
					zipCode: values.zipCode,
					dateBusinessBegan: null,
					yearsInBusiness: '',
					pharmacyType: 'Retail',
					npiNumber: values.npiNumber,
					mckessonId: values.primaryAccountNumber,
					deaNumber: values.dea,
					deaExpDate: null,
					hinNumber: '',
					hinExpDate: null,
					stateLicense: values.statePharmacyLicense,
					stateLicenseExpDate: null,
					stateIdNumber: '',
					stateIdExpDate: null,
					federalTaxNumber: '',
					federalTaxExpDate: null,
					dunsNumber: '',
					ncpdpNumber: values.ncpdp,
					medicarePartB: '',
					stateSellersPermit: '',
					pharmacyAddressIsShippingAddress: true,
					shippingAddress: values.streetAddress,
					shippingCity: values.city,
					shippingState: values.state,
					shippingZip: values.zipCode,
					multipleShipToFacilities: false,
					relatedToOtherFacilities: false,
					relatedToOtherFacilitiesList: '',
					gln: '',
					freeTrial: {
						isApciPvacTier: false,
						isIpcPvacTier: true,
						isFreeTrial: false,
						isExpired: false,
						startsAt: new Date(),
						createdAt: new Date(),
					},
					brandsBudget: 0,
					genericsBudget: 0,
					dispensing: {
						fillOutOfStateCustomers: false,
						outOfStateCustomersCount: '',
						managementSystem: values.pharmacyManagementSystem,
					},
					owner: {
						firstName: values.firstName,
						lastName: values.lastName,
						address: values.streetAddress,
						city: values.city,
						state: values.state,
						zip: values.zipCode,
						phone: values.pharmacyPhoneNumber,
						social: '',
						percentOwned: '100',
						ownershipType: '',
						registerType: '',
						registerTypeOther: '',
					},
					primary: {
						name: 'McKesson',
						address: '',
						city: '',
						zip: '',
						state: '',
						phone: '',
						fax: '',
						psao: 'IPC',
						psoaOther: '',
					},
					banking: {
						bankName: '',
						bankAddress: '',
						bankCity: '',
						bankZip: '',
						bankState: '',
						accountOfficer: '',
						accountNumber: '',
						routingNumber: '',
						bankPhone: '',
						accountType: 'Checking',
						initialChargeAmount: 0,
						initialChargeAmountDesc: '',
						monthlyChargeAmount: 0,
						receiveInvoices: '',
						accountToBeDebited: '',
						accountToBeDebitedOther: ''
					},
					payments: {
						cardNumber: '',
						cardType: '',
						cardExpDate: '',
						cardCvc: '',
						cardZip: '',
						bloodworth_secondary: {
							paymentMethod: 'ACH'
						},
						billingAddress: '',
						billingCity: '',
						billingState: '',
						billingZip: '',
						bluepax_secondary: {
							paymentMethod: 'ACH'
						},
						capital_secondary: {
							paymentMethod: 'ACH'
						},
						rx_supply_secondary: {
							paymentMethod: 'ACH'
						},
						keysource_secondary: {
							paymentMethod: 'ACH'
						},
						toprx_secondary: {
							paymentMethod: 'ACH'
						},
						auburn_secondary: {
							paymentMethod: 'ACH'
						},
						ipc_secondary: {
							paymentMethod: 'ACH'
						},
						quest_secondary: {
							paymentMethod: 'ACH'
						},
						masters_secondary: {
							paymentMethod: 'ACH'
						},
						mutual_drug_secondary: {
							paymentMethod: 'ACH'
						}
					},
					tradeRefSameAsPw: true,
					tradeReference1: {
						name: '',
						address: '',
						city: '',
						state: '',
						zip: '',
						phone: '',
						accountNumber: ''
					},
					tradeReference2: {
						name: '',
						address: '',
						city: '',
						state: '',
						zip: '',
						phone: '',
						accountNumber: ''
					},
					convicted: undefined,
					bankruptcy: undefined,
					subscription: SubscriptionType.BEST,
				};

				await api.patch.onboard(onboardId, {
					email: values.pharmacyEmail,
					npiNumber: Number(values.npiNumber),
				});

				await api.post.pharmacy(pharmacyPostData);

				setIsSubmitted(true);
				enqueueSnackbar('Pharmacy created successfully!', { variant: 'success' });
			} catch (error: any) {
				if (error?.status === 409) {
					enqueueSnackbar('There is already a pharmacy registered with that NPI number.', { variant: 'error' });
				} else {
					console.error(error);
					enqueueSnackbar('Something went wrong during submission. Please try again. ' + error.message, { variant: 'error' });
				}
			}
		},
	});

	const handleStep1Complete = async () => {
		// Validate only step 1 fields
		const step1Fields = [
			'firstName', 'lastName', 'position', 'pharmacyName', 'npiNumber', 'phone', 'email',
			'termsAccepted', 'reCaptchaToken'
		];

		// Only include password fields if not linking an existing account
		if (!formik.values.isMultiStoreOwner) {
			step1Fields.push('password', 'confirmPassword');
		}

		try {
			await step1ValidationSchema.validate(formik.values, { abortEarly: false });
			setCurrentStep(2);
			// Update validation schema for step 2
			formik.setFieldValue('validationSchema', step2ValidationSchema);
		} catch (_error: any) {
			// Mark step 1 fields as touched to show errors
			const touchedFields: any = {};
			step1Fields.forEach(field => {
				touchedFields[field] = true;
			});
			formik.setTouched({ ...formik.touched, ...touchedFields });
		}
	};

	const handleStep2Complete = async () => {
		// Validate only step 2 fields
		const step2Fields = [
			'companyLegalName', 'pharmacyDbaName', 'pharmacyPhoneNumber', 'pharmacyEmail',
			'streetAddress', 'city', 'state', 'zipCode', 'dea', 'pharmacyManagementSystem',
			'primaryAccountNumber', 'ipcAccountNumber'
		];

		try {
			await step2ValidationSchema.validate(formik.values, { abortEarly: false });
			formik.handleSubmit();
		} catch (_error: any) {
			// Mark step 2 fields as touched to show errors
			const touchedFields: any = {};
			step2Fields.forEach(field => {
				touchedFields[field] = true;
			});
			formik.setTouched({ ...formik.touched, ...touchedFields });
		}
	};

	const handleBackToStep1 = () => {
		setCurrentStep(1);
	};

	// Show success screen after submission
	if (isSubmitted) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
				<Grid
					container
					direction='column'
					alignItems='center'
					justifyContent='center'
					sx={{ flex: 1, padding: 0, background: 'url(/img/pharmacy_1.jpg) no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }}
				>
					<Card
						sx={{ width: '100%', maxWidth: 800, margin: 'auto', boxShadow: 10, borderRadius: 2 }}
					>
						<Box sx={{ p: 4, textAlign: 'center' }}>
							<Typography variant="h4" gutterBottom color="primary">
								Registration Complete!
							</Typography>
							<Typography variant="h6" color="textSecondary" sx={{ mb: 3 }}>
								What&apos;s next? You will receive an email shortly to electronically sign the User Agreement and Business Associate Agreement.
							</Typography>
							<Typography variant="body1" color="textSecondary">
								Thank you for choosing Pharmacy Marketplace. We&apos;re excited to have you on board!
							</Typography>
						</Box>
					</Card>
				</Grid>
			</Box>
		);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
			<Grid
				container
				direction='column'
				alignItems='center'
				justifyContent='center'
				sx={{ flex: 1, padding: 0, background: 'url(/img/pharmacy_1.jpg) no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }}
			>
				<IPCNav currentStep={currentStep} />
				<Card
					sx={{ width: '100%', maxWidth: 1200, margin: 'auto', boxShadow: 10, borderRadius: 2 }}
				>
					{currentStep === 1 && (
						<IPCStep1 
							onComplete={handleStep1Complete}
							formik={formik}
							ipcPositions={ipcPositions}
							onboardJWT={onboardJWT}
							onboardId={onboardId}
							getOnboardJWT={getOnboardJWT}
						/>
					)}
					{currentStep === 2 && (
						<IPCStep2 
							onComplete={handleStep2Complete}
							onBack={handleBackToStep1}
							formik={formik}
							pharmacyManagementSystems={pms.filter((option) => option.name !== 'Select').map((option) => option.name)}
						/>
					)}
				</Card>
			</Grid>
		</Box>
	);
}); 